import React, { useCallback, useEffect, useMemo } from "react";
// Style imports
import { QueryClientProvider } from "@tanstack/react-query";
import jwtDecode from "jwt-decode";
// External imports
import { observer } from "mobx-react";
import { IntercomProvider } from "react-use-intercom";

// Relative imports
import { ClientQueryConfig } from "@formant/client-query";
// Internal alias imports
import { namespace } from "@formant/core/common/config";
import { logger } from "@formant/core/common/logger";
import { IFormantJwtPayload } from "@formant/core/model/IFormantJwtPayload";
import {
    Environment,
    FormantClientContextProvider,
    NavigationType,
} from "@formant/rectangle-sdk";
import {
    defaultDarkColorSet,
    ThemeCustomizerProvider,
    ThemeProvider,
    Toaster,
} from "@formant/ui-kit";
import { Theme } from "@formant/ui/component/common/Theme";

import { intercomAppKey } from "../config";
import { LoggerContextProvider } from "../context/LoggerContext";
import { RouterContextProvider } from "../context/RouterContext";
import { StoreProvider } from "../context/StoreContext";
import { queryClient } from "../lib/query-client";
import { INavigateOptions } from "../router/INavigateOptions";
import { routes } from "../router/routes";
import {
    adapterStore,
    annotationTemplateStore,
    appStore,
    authStore,
    channelStore,
    commandStore,
    commandTemplateStore,
    connectedStore,
    customModuleStore,
    deviceConfigurationStore,
    deviceConfigurationTemplateStore,
    deviceDetailsStore,
    deviceStore,
    eventStore,
    groupStore,
    metadataStore,
    observeStore,
    onDemandStore,
    onlineDevicesStore,
    operateStore,
    organizationStore,
    queryStore,
    roleStore,
    routerStore,
    rtcSignalingStore,
    signalingStore,
    ssoStore,
    streamCurrentValueStore,
    streamStore,
    tagTemplateStore,
    teamStore,
    usageMetricsStore,
    userStore,
    viewStore,
} from "../stores";
import { FeatureFlagLocalStorageManager } from "../utils/FeatureFlagLocalStorageManager/FeatureFlagLocalStorageManager";
import { useCoherenceRedirect } from "../utils/hooks/useCoherenceRedirect";
import {
    AppPage,
    FormantStyles,
    StyledApp,
    StyledPageAndBanner,
    StyledTypographyCore,
} from "./App.styles";
import { FormantWebModuleClient } from "./common/FormantWebModuleClient";
import { HotkeysBinding } from "./common/hotkey/HotkeysBinding";
import { ConfirmPopup } from "./ConfirmPopup";
import { IntercomInjector } from "./IntercomInjector";
import { OutageBanner } from "./OutageBanner";
import { Snackbar } from "./Snackbar";
import { Tooltip } from "./Tooltip";

import "./App.scss";

const stores = {
    adapterStore,
    annotationTemplateStore,
    appStore,
    authStore,
    channelStore,
    commandStore,
    commandTemplateStore,
    connectedStore,
    customModuleStore,
    deviceConfigurationStore,
    deviceConfigurationTemplateStore,
    deviceDetailsStore,
    deviceStore,
    eventStore,
    groupStore,
    metadataStore,
    observeStore,
    onDemandStore,
    onlineDevicesStore,
    operateStore,
    organizationStore,
    queryStore,
    roleStore,
    routerStore,
    rtcSignalingStore,
    signalingStore,
    ssoStore,
    streamCurrentValueStore,
    streamStore,
    tagTemplateStore,
    teamStore,
    usageMetricsStore,
    userStore,
    viewStore,
};

// turn off console.warn  and log in prod and stage
if (namespace === "prod" || namespace === "stage") {
    console.warn = () => {};
}

routerStore.setRoutes(routes);

FeatureFlagLocalStorageManager.initializeFromQueryParams();

// app.formant.io is locked to the built-in Default theme; theming lives only in coherence/platform now
const forcedDefaultThemeList = [defaultDarkColorSet];
const noopSaveTheme = () => {};

const AppCore = observer(function App() {
    const formantClient = useMemo(() => new FormantWebModuleClient(), []);

    useCoherenceRedirect();

    useEffect(() => {
        const token = authStore.refreshTokenFromStorage;
        let userId = null;
        if (token) {
            // decode the token
            const decodedToken = jwtDecode<IFormantJwtPayload>(token);
            userId = decodedToken["formant:claims"]?.userId;
        }
        window.parent?.postMessage(
            {
                type: "auth-loaded",
                userId,
            },
            "*",
        );
    }, []);

    useEffect(() => {
        void routerStore.navigate();
        const handlePopState = () => routerStore.navigate();
        window.addEventListener("popstate", handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [routerStore, routes]);

    const hideIntercom = useCallback(() => {
        window.Intercom("update", {
            hide_default_launcher: true,
        });
    }, []);

    const routerNavigate = useCallback(
        (to: string, options?: INavigateOptions) =>
            routerStore.navigate(to, options),
        [],
    );

    const handleNavigation = async (type: NavigationType, id: string) => {
        switch (type) {
            case "device":
                await routerStore.navigate(`/devices/${id}`);
                break;
            default:
                console.warn(`Unknown navigation type: ${type}`);
        }
    };

    // Add route change listener for TheopolisChat updates
    useEffect(() => {
        const handleRouteChange = () => {
            // No need to do anything in this handler
            // The popstate event will be detected in the TheopolisChatContext
        };

        routerStore.registerRouteChangeHandler(handleRouteChange);
        return () => {
            routerStore.unregisterRouteChangeHandler(handleRouteChange);
        };
    }, []);

    useEffect(() => {
        const fetchToken = async () => {
            ClientQueryConfig.getInstance().init(
                () => authStore.token(),
                process.env.NEXT_PUBLIC_API_URL ||
                    process.env.API_BASE_URL ||
                    "https://api-dev.formant.io",
            );
        };

        fetchToken()
            .then(() => {
                console.log("Token fetched");
            })
            .catch(() => {
                console.log("Error fetching token");
            });
    }, [authStore.currentToken]);

    const page = (
        <StyledPageAndBanner>
            {organizationStore.outageBannerEnabled() && <OutageBanner />}
            <AppPage>{routerStore.page}</AppPage>
        </StyledPageAndBanner>
    );

    const content = (
        <LoggerContextProvider logger={logger}>
            <StoreProvider stores={stores}>
                <RouterContextProvider
                    navigate={routerNavigate}
                    routes={routes}
                >
                    <QueryClientProvider client={queryClient}>
                        <IntercomInjector />
                        <style>{organizationStore.getWhiteLabelStyle()}</style>
                        <StyledApp>
                            {page}
                            <Tooltip />
                            <Snackbar />
                            <ConfirmPopup />
                            <HotkeysBinding />
                            <StyledTypographyCore>
                                <div id="portal" />
                            </StyledTypographyCore>
                        </StyledApp>
                    </QueryClientProvider>
                </RouterContextProvider>
            </StoreProvider>
        </LoggerContextProvider>
    );

    return (
        <FormantClientContextProvider
            client={formantClient}
            environment={namespace as Environment}
            onNavigate={handleNavigation}
            featureFlags={{
                betaUi: true,
                explorations: organizationStore.getAiEnabled(),
                analytics: true,
                newWorkflows: false,
                mixedModulesEnabled: true,
                alarmConsoleEnabled: true,
                v2CustomModulesEnabled: true,
                commandsModuleEnabled: true,
                sceneNativeEnabled: true,
                aiInsightsEnabled: organizationStore.getAiEnabled(),
                aiEnabled: organizationStore.getAiEnabled(),
            }}
        >
            {/* -v2 keys force every user back to Default, ignoring any legacy light/custom selection */}
            <ThemeProvider
                defaultTheme="dark"
                storageKey="formant-ui-platform-theme-v2"
            >
                <Theme mode="dark">
                    <ThemeCustomizerProvider
                        onSave={noopSaveTheme}
                        themeList={forcedDefaultThemeList}
                        storageThemeKey="formant-ui-platform-custom-theme-v2"
                    >
                        {intercomAppKey ? (
                            <IntercomProvider
                                appId={intercomAppKey}
                                onHide={hideIntercom}
                            >
                                {content}
                            </IntercomProvider>
                        ) : (
                            content
                        )}
                        <FormantStyles />
                        <Toaster />
                    </ThemeCustomizerProvider>
                </Theme>
            </ThemeProvider>
        </FormantClientContextProvider>
    );
});

export { AppCore as App };
